import React, { useState } from "react";
import axios from "axios";
import {
  Card,
  Flex,
  Space,
  Text,
  LoadingOverlay,
  Loader,
  Box,
} from "@mantine/core";
import { useParams, Route, Routes } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ErrorView,
  PageTitle,
  Subnav,
  EntitySubtitles,
} from "@components/shared";
import {
  BulkCommunicationMailerStatus,
  BulkCommunicationMessages,
  BulkCommunicationStatus,
  BulkCommunicationTemplate,
  BulkCommunicationSms,
} from "@components/BulkCommunication";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function BulkCommunicationDetailReadOnly() {
  const queryClient = useQueryClient();
  const params = useParams();
  const { id } = params;
  const queryKey = ["detail", id];
  const statusQueryKey = ["status", id];
  const pollingStateKey = ["polling-state", id];
  const userSchedulingQueryKey = ["user-scheduling-state", id];
  const userSchedulingPollingKey = ["user-scheduling-polling", id];
  const [refreshMessages, setRefreshMessages] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { failureReason, data: info } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const requestData = {
        bulk_communicator_id: id,
      };

      if (isInitialLoad) {
        requestData.initial_load = true;
      }

      return axios
        .post(`/retrieve-bulk-communicator/${id}/`, requestData)
        .then(({ data }) => {
          setIsInitialLoad(false);
          return data.response[0];
        })
        .catch((err) => {
          throw err;
        });
    },
  });

  const { data: statusInfo } = useQuery({
    queryKey: statusQueryKey,
    refetchInterval: () => {
      const pollingState = queryClient.getQueryData(pollingStateKey);
      return pollingState ? 5000 : null;
    },
    queryFn: async () =>
      axios
        .post(`/bulk-communicators/${id}/data-grabber/`, {
          bulk_communicator_id: id,
          context: "polling",
        })
        .then(({ data }) => {
          const response = data.response[0];
          if (
            response.poll_for_mailer_status ||
            response.poll_for_texter_status ||
            response.poll_for_status
          ) {
            queryClient.setQueryData(pollingStateKey, true);
          } else {
            queryClient.setQueryData(pollingStateKey, false);
          }
          if (response.adding_messages_complete !== refreshMessages) {
            setRefreshMessages(response.adding_messages_complete);
          }
          return response;
        })
        .catch((err) => {
          queryClient.setQueryData(pollingStateKey, false);
          throw err;
        }),
  });

  const { data: userSchedulingInfo } = useQuery({
    queryKey: userSchedulingQueryKey,
    refetchInterval: () => {
      const pollingState = queryClient.getQueryData(userSchedulingPollingKey);
      return pollingState === true ? 2500 : null;
    },
    queryFn: async () =>
      axios
        .post(`/bulk-communicators/${id}/data-grabber/`, {
          bulk_communicator_id: id,
          context: "user_scheduling_polling",
        })
        .then(({ data }) => {
          const response = data.response[0];
          if (response.scheduling_users) {
            queryClient.setQueryData(userSchedulingPollingKey, true);
          } else {
            queryClient.setQueryData(userSchedulingPollingKey, false);
          }
          return response;
        })
        .catch((err) => {
          queryClient.setQueryData(userSchedulingPollingKey, false);
          throw err;
        }),
  });

  const fetchData = () => queryClient.invalidateQueries([statusQueryKey]);
  // queryClient.invalidateQueries([queryKey, statusQueryKey]);

  if (failureReason) {
    return <ErrorView text={failureReason} />;
  }

  if (!info || !statusInfo) return <Loader />;

  const isEmail = info.variety === 1;
  const isTexting = info.variety === 2;

  const links = [
    { to: `/bulk-communication/${id}`, text: "Detail " },
    { to: `/bulk-communication/${id}/template`, text: "Template" },
    { to: `/bulk-communication/${id}/users`, text: "Users" },
  ];

  return (
    <div style={{ position: "relative" }}>
      <Flex>
        <div>
          <PageTitle
            title={info.title}
            subtitle={<React.Fragment>Bulk Communication</React.Fragment>}
            icon={entityIcon.send(14)}
            iconColor={entityColor.send}
            style={{ marginBottom: 0 }}
          />
          {info.created_by && (
            <PageTitle
              icon={
                info.created_by.class_name === "Manager"
                  ? entityIcon.manager()
                  : entityIcon.admin()
              }
              iconColor={
                info.created_by.class_name === "Manager"
                  ? entityColor.manager
                  : entityColor.admin
              }
              subtitle={`created by ${info.created_by.full_name} (${info.created_by.class_name})`}
              style={{ marginBottom: 0 }}
            />
          )}
          <EntitySubtitles
            info={{
              ...info,
              campaign_effort_name: info.campaign_effort_title,
              campaign_effort_id: info.campaign_effort_id,
            }}
          />
        </div>
      </Flex>
      <Space mt="md" />
      <Subnav links={links} />
      <div style={{ position: "relative" }}>
        <LoadingOverlay
          visible={userSchedulingInfo?.scheduling_users}
          loaderProps={{
            children: (
              <Flex justify="center" align="center" gap="xs" direction="column">
                <Loader />
                <Text align="center">
                  {userSchedulingInfo?.message || "Loading..."}
                </Text>
              </Flex>
            ),
          }}
        />
        <Routes>
          <Route
            exact
            path="/"
            element={
              <Box>
                <Flex gap="xs" direction={{ base: "column", md: "row" }}>
                  <div style={{ flexGrow: 1, minWidth: "50%" }}>
                    <Card>
                      <BulkCommunicationStatus
                        id={id}
                        fetchData={fetchData}
                        fetchStatus={() => true}
                        status={info.status}
                        info={info}
                        statusInfo={statusInfo}
                        reqData={{
                          bulk_communication_id: id,
                        }}
                      />
                    </Card>
                  </div>
                  {info.enqueued_at && (
                    <div style={{ flexGrow: 1, minWidth: "50%" }}>
                      <Card>
                        <BulkCommunicationMailerStatus
                          id={id}
                          statusInfo={statusInfo}
                          variety={info.variety}
                        />
                      </Card>
                    </div>
                  )}
                </Flex>
              </Box>
            }
          />
          <Route
            path="template"
            element={
              <div>
                {isEmail && (
                  <BulkCommunicationTemplate
                    locationId={info.location_id}
                    orgId={info.organization_id}
                    bulkCommunicationId={id}
                    editable={false}
                  />
                )}
                {isTexting && (
                  <BulkCommunicationSms
                    id={id}
                    fetchData={fetchData}
                    fetchStatus={() => true}
                    status={info.status}
                    effortId={info.campaign_effort_id}
                    templateId={info.template_id}
                    editable={false}
                  />
                )}
              </div>
            }
          />
          <Route
            path="users"
            element={
              <div>
                <BulkCommunicationMessages
                  id={id}
                  variety={info.variety}
                  refresh={refreshMessages}
                  info={info}
                />
              </div>
            }
          />
        </Routes>
      </div>
    </div>
  );
}
